import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AppNavbar from './components/AppNavbar';
import SideMenu from './components/SideMenu';
import AppTheme from './shared-theme/AppTheme';
import Header from './components/Header';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';
import { getCurrentUser, getUserDetails, signOut } from './api/auth';

// Import Pages
import Home from './pages/Home';
import Analytics from './pages/Analytics';
import RegisterPage from './pages/Register';
import SignInSide from './pages/SignInSide';
import Report from './pages/Report';
import Settings from './pages/Settings';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const pageTitles = {
  '/': 'Startseite',
  '/analytics': 'Analyse',
  '/register': 'Zahlprotokoll',
  '/report': 'Kassenbericht',
  '/settings': 'Einstellungen',
  '/signin': 'Anmelden',
};

// ProtectedRoute to check if the user is an admin
const ProtectedRoute = ({ userDetails, children }) => {
  if (userDetails?.role !== 'admin') {
    return <Navigate to="/" />;
  }
  return children;
};

function MainContent({ selectedDate, setSelectedDate, userDetails, onLogout }) {
  const location = useLocation();
  const pageTitle = pageTitles[location.pathname] || 'Default Title';

  return (
    <Box
      component="main"
      sx={(theme) => ({
        flexGrow: 1,
        backgroundColor: theme.vars
          ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
          : theme.palette.background.default,
        overflow: 'auto',
      })}
    >
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          mx: 3,
          pb: 5,
          mt: { xs: 8, md: 0 },
        }}
      >
        <Header selectedDate={selectedDate} setSelectedDate={setSelectedDate} pageTitle={pageTitle} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute userDetails={userDetails}>
                <Analytics selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report"
            element={
              <Report selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute userDetails={userDetails}>
                <Settings onLogout={onLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/register"
            element={<RegisterPage selectedDate={selectedDate} setSelectedDate={setSelectedDate} />}
          />
          <Route path="/signin" element={<SignInSide onLoginSuccess={() => window.location.reload()} />} />
        </Routes>
      </Stack>
    </Box>
  );
}

function RedirectToFacebook() {
  useEffect(() => {
    window.location.href = 'https://www.facebook.com/profile.php?id=100080121740842'; // Replace with your actual Facebook page
  }, []);
  return null;
}

export default function App(props) {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const details = await getUserDetails();
        setUserDetails(details);
      } catch (error) {
        console.error('Error checking user:', error.message);
        setUserDetails(null);
      } finally {
        setLoading(false);
      }
    };
    checkUser();
  }, []);

  const handleLoginSuccess = async () => {
    const details = await getUserDetails();
    setUserDetails(details);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setUserDetails(null);
    } catch (error) {
      console.error('Logout error:', error.message);
    }
  };

  if (loading) {
    return (
      <AppTheme {...props} themeComponents={xThemeComponents}>
        <CssBaseline enableColorScheme />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <h2>Loading...</h2>
        </Box>
      </AppTheme>
    );
  }

  const hostname = window.location.hostname.toLowerCase();
  const isAdminSubdomain = hostname.startsWith('admin.'); // Matches admin.domain.com or admin.anything

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Router>
        {isAdminSubdomain ? (
          <Box sx={{ display: 'flex' }}>
            <AppNavbar
              userDetails={userDetails}
              onLogout={handleLogout}
              selectedDate={selectedDate}
              role={userDetails?.role}
            />
            {userDetails ? (
              <>
                <SideMenu role={userDetails?.role} />
                <MainContent
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  userDetails={userDetails}
                  onLogout={handleLogout}
                />
              </>
            ) : (
              <Routes>
                <Route path="/signin" element={<SignInSide onLoginSuccess={handleLoginSuccess} />} />
                <Route path="*" element={<Navigate to="/signin" />} />
              </Routes>
            )}
          </Box>
        ) : (
          <Routes>
            <Route path="*" element={<RedirectToFacebook />} />
          </Routes>
        )}
      </Router>
    </AppTheme>
  );
}